const PrivacyPolicy = () => {
  return (
    <div className="content-container">
      <h3>Privacy Policy</h3>
      <div className="base-text">
        <b>Last Updated: April 1, 2026</b>
      </div>
      <br></br>
      <div className="base-text">
        Welcome to Oxford Document Management’s online portal. We understand the
        importance of your privacy and we take our responsibility to protect it
        seriously. This Privacy Policy (the “Policy”) describes the types of
        information we collect from you through your access and use of our
        online software application (the “Platform”). This Policy also describes
        how we use and disclose such information, and certain rights and options
        you have regarding your information.
      </div>
      <br></br>
      <div className="base-text">
        By providing us with personal information or using the Platform, you
        expressly consent to the information-handling practices described in
        this Policy. If you do not agree with this Policy, please do not use the
        Platform or provide us with your information.
      </div>
      <br></br>
      <h5>
        <b>Important Information About This Policy</b>
      </h5>
      <div className="base-text">
        While we always aim to provide complete and transparent information
        about how we process your personal information, we reserve the right to
        amend or update this Policy from time to time, or to create additional
        policies, in order to accurately reflect changed circumstances or new
        legal requirements. As a result, it is important that you read this
        Policy closely so that you are fully aware of how and why we are using
        your personal information.
      </div>
      <br></br>
      <div className="base-text">
        <div className="base-text">
          To assist us in ensuring that we can provide the most accurate
          information possible, we ask that you check this Policy for updates.
          We also ask that, where we have an ongoing relationship with you and
          any of the data you have provided becomes no longer accurate, you keep
          us informed of this so we can amend our records accordingly.
        </div>
      </div>
      <br></br>
      <h5>
        <b>Personal Information We May Collect or Receive About You </b>
      </h5>
      <div className="base-text">
        For the purposes of this Policy, “personal information” is any
        information that identifies, relates to, or can be used to contact a
        particular individual. The types of personal information we collect and
        receive about you are described below.
      </div>
      <br></br>
      <div className="base-text">A. Information You Provide</div>
      <div className="base-text">
        Personal Information we collect from you includes:{" "}
      </div>
      <ul>
        <li>
          <b>Contact information</b> – first name, last name, email address
          (personal, institutional, or business), mailing address (personal,
          institutional, or business), billing address (personal, institutional,
          or business), telephone number (personal, institutional, or
          business).{" "}
        </li>
        <li>
          <b>Account information</b> – if there is an ability to register to use
          our Platform, your username, account password, and other information
          that we may request or that you may provide relating to your account.
        </li>
        <li>
          <b>Candidate information</b> – if you are providing details about a
          candidate (whether that is yourself or someone for whom you are a
          reference), we will collect the information that you supply in
          response to questionnaires, surveys, or other requests for
          information.
        </li>
        <li>
          <b>Transactional information</b> – details about your, or your
          company’s, transactions with us, including method of payment, payments
          received, payment details, transaction history, and other information
          relating to the services purchased by you or your company.{" "}
        </li>
        <li>
          <b>Marketing information</b> – details regarding informational and
          promotional materials you may have requested or received from us, the
          services or products in which you are interested, your receipt of
          promotional communications, and information on your marketing or
          communication preferences.
        </li>
        <li>
          <b>Customer support information</b> – details of any communications or
          inquiries you submit to us, including technical or customer support
          requests, through email, calls, chat, or other features available on
          our Platform.
        </li>
        <li>
          <b>Device and usage information</b> – details regarding how and when
          you use our Platform, including the device used to connect to our
          Platform, your IP address and device identifier, the frequency and
          duration of your usage, the pages you view, what websites or search
          terms referred you to our Platform, and information about your
          interaction with our Platform. We do not use IP addresses to
          personally identify users unless there is an active investigation of
          misuse or fraudulent activity.
        </li>
      </ul>
      <div className="base-text">
        We collect this information when you access, use, or navigate our
        Platform, fill in forms on our Platform, request information from us or
        about our products or services, communicate with us (including by phone,
        email, chatbot, or otherwise), interact with features of our Platform,
        visit or engage with our social media pages, participate in surveys or
        sponsored activities, or otherwise provide us with personal
        information.{" "}
      </div>
      <br></br>
      <div className="base-text">
        Please note that we may aggregate or anonymize the foregoing types of
        data such that they are no longer capable of identifying you, in which
        case they are no longer considered “personal information.”
      </div>
      <br></br>
      <div className="base-text">B. Automatically Collected Information</div>
      <br></br>
      <div className="base-text">
        Like most online operators, we use various technologies to automatically
        collect information about you.
      </div>
      <br></br>
      <div className="base-text">
        When you visit our Platform, we gather usage information made available
        to us by your computer, mobile, or other access device that tells us how
        you are using the Platform including, but not limited to, site or page
        views, key stroke data, mouse movement, scrolling, clicks, and typing.
        We use cookies, pixels, web beacons, GIFs, embedded scripts, and similar
        tools to gather usage information.
      </div>
      <br></br>
      <div className="base-text">
        Some tracking technologies that are deployed on our Platform come from
        us – they are operated by us (or our service providers) and at our
        direction for our benefit.
      </div>
      <br></br>
      <div className="base-text">
        Other tracking technologies may be supplied by our third-party vendors
        and partners.{" "}
        <div style={{ textDecoration: "underline" }}>
          These third-party vendors or partners may collect your information
          instantaneously and simultaneous with our collection of such
          information and they may use your information for their own, or
          others’, purposes.
        </div>
      </div>
      <br></br>
      <div className="base-text">
        You have some choices with respect to the use of analytical or
        performance technologies, functionality technologies, and
        targeting/advertising technologies:
      </div>
      <ul>
        <li>
          <b>Declining Cookies</b> – Depending on your browser or device, you
          may have the option to set the browser to accept all cookies, reject
          all cookies, notify you when a cookie is set, or delete cookies. Each
          browser and device are different, so we recommend you evaluate the
          tools and settings available in your browser or device, as well as any
          available instructions for the same. Please note that if you disable
          or delete cookies, you may not be able to access or use certain
          features of our Platform. The National Advertising Initiative also
          makes resources available to assist consumers in opt-out of certain
          tailored online ads, which you can access here:{" "}
          <a href=" https://optout.networkadvertising.org/?c=1">
            https://optout.networkadvertising.org/?c=1
          </a>
          . You can also use the Digital Advertising Alliance’s Consumer Choice
          Tool located at{" "}
          <a href="https://optout.aboutads.info/?c=2&lang=EN">
            https://optout.aboutads.info/?c=2&lang=EN
          </a>
          .{" "}
        </li>
        <li>
          <b>Google Analytics</b> – As discussed above, we use Google products
          in connection with the Platform. If you would like to refrain from
          having your data collected by Google, Google has developed an opt-out
          browser that you can use – see{" "}
          <a href=" https://tools.google.com/dlpage/gaoptout">
            {" "}
            https://tools.google.com/dlpage/gaoptout
          </a>
          .{" "}
        </li>
        <li>
          <b>Do Not Track Mechanisms</b> – Please note that our Platform does
          not honor “Do Not Track” signals, and such signals will not impact the
          operation of the Platform.{" "}
        </li>
      </ul>
      <br></br>
      <div className="base-text">
        Note the information collected by us via tracking technologies may be
        combined with other information from or about you, and disclosed to our
        vendors or service providers who may combine the information with
        information they have collected.{" "}
      </div>
      <br></br>
      <div className="base-text">C. Information from Other Sources</div>
      <br></br>
      <div className="base-text">
        We may obtain both personal and non-personal information about you from
        affiliates, business partners, vendors, contractors, suppliers, and
        other third parties and add it to other information we have collected.
        We, and the third parties we engage, may combine information we collect
        from you over time, and across your use of our Platform, with
        information obtained from other sources. This helps us improve the
        information’s overall accuracy and completeness, and also helps us
        better tailor our interactions with you.{" "}
      </div>
      <br></br>
      <div className="base-text">
        <b>How We Use Your Information </b>
      </div>
      <br></br>
      <div className="base-text">
        We (or the service providers acting on our behalf) use the personal
        information identified above for the following purposes:
      </div>
      <ul>
        <li>
          Providing and optimizing your experience using our Platform and
          ensuring that our content is presented to you in the most effective
          manner. This includes by leveraging third-party service providers who
          may assist with or facilitate our chat features and functionalities to
          gather analytics and other insights into the users who visit our
          Platform.{" "}
        </li>
        <li>
          Communicating with you and responding to your inquiries and
          communicating regarding our services, our agreements with you or your
          company, and other issues.
        </li>
        <li>
          Negotiating, entering into, and managing our relationship and
          agreements with you or your company.
        </li>
        <li>
          Sending you promotional or informational communications and
          solicitations, tracking your marketing preferences, and for our
          internal marketing purposes.
        </li>
        <li>
          Managing customer service issues, including issues relating to the
          performance of our Platform, and other customer feedback.
        </li>
        <li>
          Developing, updating, and improving our business, software
          applications, customer service, and customer experience, and otherwise
          improving our knowledge and insights regarding customers.
        </li>
        <li>
          Preventing and detecting fraud, financial crime, hacking activities,
          security breaches, and other unlawful activities in connection with
          the Platform or the purchase or use of our products.
        </li>
        <li>
          Enforcing our agreements with you or your company, complying with our
          legal or regulatory obligations, and defending or advancing legal
          claims.
        </li>
        <li>
          Notifying you about changes to our Platform, the terms governing our
          Platform, or material changes to this Policy.
        </li>
        <li>
          Providing you with surveys or otherwise soliciting feedback from you.
        </li>
        <li>
          Performing other functions as otherwise described to you at the time
          of collection or to which you otherwise consent.
        </li>
      </ul>
      <br></br>
      <div className="base-text">
        <b>Our Marketing Efforts</b>
      </div>
      <br></br>
      <div className="base-text">
        We may send you direct marketing communications from time to time
        including news, updates, offers, and other announcements relating to our
        business. We may also disclose your information to those of our
        affiliates and related entities who have products or services that we
        believe may be of interest to you. We will only send marketing messages
        where the law allows us to do so and, in the event that you no longer
        wish to receive marketing communications from us, then you can always
        unsubscribe using the option provided in the communication you have
        received (e.g., in the footer of the email) or by contacting us using
        the information below.
      </div>
      <br></br>
      <div className="base-text">
        <b>Disclosure of Your Information </b>
      </div>
      <br></br>
      <div className="base-text">
        When the information we collect is aggregated, anonymized, or otherwise
        does not identify you, it is no longer considered “personal
        information.” We may use such information for any purpose or disclose it
        to third parties, to the extent permitted by applicable law.
      </div>
      <br></br>
      <div className="base-text">
        For more information about your options relating to your personal
        information and your communication preferences, see “Your Choices About
        Collection and Use of Your Information” below.{" "}
      </div>
      <br></br>
      <div className="base-text">
        In addition, we may disclose your personal information to the following
        types of individuals or entities:
      </div>
      <ul>
        <li>
          Service providers and third-party vendors we use to administer our
          business and as are necessary for the provision of our Platform and
          performing any contract with you or your company. For example, our IT
          providers, secure payment processing providers, email, chat, and phone
          communication providers, customer contact center providers, insurance
          providers, and document storage providers.{" "}
        </li>
        <li>
          Affiliates, related entities, and business partners who may offer
          products or services that are relevant to you.{" "}
        </li>
        <li>
          Analytics vendors in order to understand our Platform traffic and
          usage patterns, optimize our Platform, and identify potential new
          customers. Some of these vendors provide us with technologies that are
          deployed on our Platform and automatically collect information from
          and about your use of the Platform.{" "}
        </li>
        <li>
          Regulatory and governmental authorities, law enforcement agencies, and
          courts, as necessary to comply with applicable laws and regulations,
          respond to a subpoena, search warrant, or other lawful request for
          information, or to otherwise protect our rights.{" "}
        </li>
        <li>
          Our professional advisors, such as lawyers, accountants, and other
          similar advisors.{" "}
        </li>
        <li>
          Buyers or other successors prior to or in the event of a merger,
          acquisition, divestiture, restructuring, reorganization, dissolution,
          or other sale or transfer of some or all of our assets, whether as a
          going concern or as a part of bankruptcy, liquidation, or similar
          proceeding, where the information is among the assets being
          transferred.{" "}
        </li>
        <li>
          Other parties for any purpose we disclose at the time you provide the
          information.{" "}
        </li>
      </ul>
      <br></br>
      <div className="base-text">
        <b>Protection of Personal Information </b>
      </div>
      <br></br>
      <div className="base-text">
        We use appropriate administrative, technical, and physical measures to
        protect your personal information from loss, theft, and unauthorized
        use, disclosure, or modification. Please be aware that no data
        transmission over the Internet is 100% secure. While we strive to
        protect your personal information, we cannot ensure or warranty the
        security of any information you transmit to us and you do so at your own
        risk.
      </div>
      <br></br>
      <div className="base-text">
        <b>Your Choices About Collection and Use of Your Information </b>
      </div>
      <br></br>
      <div className="base-text">
        We respect your right to make choices about the ways we collect, use,
        and disclose your information, so we try to offer you meaningful choices
        regarding your personal information. Some choices you have regarding
        personal information include the following.
      </div>
      <ul>
        <li>
          <b>Marketing Emails</b> – As required by applicable laws, you can
          opt-out of receiving promotional emails from us by clicking the “opt
          out,” “unsubscribe,” or similar link in any such promotional emails
          and following the instructions provided.{" "}
        </li>
        <li>
          <b>Tracking Technologies</b> – Depending on your browser or device,
          you may have the option to set the browser to accept all cookies,
          reject all cookies, notify you when a cookie is set, or delete
          cookies. Each browser and device are different, so we recommend you
          evaluate the tools and settings available in your browser or device,
          as well as any available instructions for the same. Please note that
          if you disable or delete cookies, you may not be able to access or use
          certain features of our Platform. The National Advertising Initiative
          also makes resources available to assist consumers in opt-out of
          certain tailored online ads, which you can access here:{" "}
          <a href="https://optout.networkadvertising.org/?c=1">
            https://optout.networkadvertising.org/?c=1
          </a>
          .
        </li>
        <li>
          <b>Google Analytics</b> – As discussed above, we use Google products
          in connection with the Platform. If you would like to refrain from
          having your data collected by Google, Google has developed an opt-out
          browser that you can use – see{" "}
          <a href="https://tools.google.com/dlpage/gaoptout">
            https://tools.google.com/dlpage/gaoptout
          </a>
          .{" "}
        </li>
        <li>
          <b>Declining to Provide Information</b> – You can choose not to
          provide us with information we may request, but that may result in you
          being unable to use certain features of our Platform, request
          information about our services, or initiate other transactions with
          us.{" "}
        </li>
        <li>
          <b>Jurisdiction-Specific Choices</b> – See below for additional
          choices that may be afforded to you under the laws of your place of
          residence.
        </li>
      </ul>
      <br></br>
      <div className="base-text">
        In addition to the above, you may contact us using the details provided
        at the end of this Policy with any questions about the choices relating
        to your personal information.
      </div>
      <br></br>
      <div className="base-text">
        <b>Rights Afforded to Certain Individuals </b>
      </div>
      <br></br>
      <div className="base-text">A. California Residents</div>
      <br></br>
      <div className="base-text">
        California Civil Code § 1798.83 (California’s Shine the Light Act)
        further permits California residents to request certain information
        regarding our disclosure of personal information to third parties for
        their direct marketing purposes. If you are a California resident, you
        may ask us to refrain from providing your personal information to
        certain of our affiliates and other third parties for their marketing
        purposes. Please tell us your preference by contacting us at the contact
        information below.
      </div>
      <br></br>
      <div className="base-text">B. Nevada Residents</div>
      <br></br>
      <div className="base-text">
        Nevada residents have the right to direct us to not make any sale of
        certain personal information to third parties. We do not engage in any
        “sales” as defined by Nevada laws, but you may still contact us using
        the information below to make such a request.
      </div>
      <br></br>
      <div className="base-text">C. Residents of Other U.S. States</div>
      <br></br>
      <div className="base-text">
        The above sections of this Policy describe the categories of personal
        information processed by us, the purposes for the processing of such
        personal information, the categories of personal information that may be
        disclosed to third parties, and the categories of third parties to whom
        we may disclose personal information. We do not collect sensitive
        personal information or biometric information.
      </div>
      <br></br>
      <div className="base-text">
        Under the law of your state of residence, you may have the right to
        request (1) that we confirm that we are processing your personal
        information, (2) access to the personal information we process, (3)
        correct inaccuracies in the personal information we process, taking into
        account the nature of the personal information and the purposes of the
        processing of such personal information, (4) delete the personal
        information provided by you or obtained about you, subject to certain
        exceptions under applicable laws, (5) a copy of the personal information
        that we process, subject to certain limitations and exceptions, or (6)
        opt out of the processing of your personal information for the purposes
        of targeted advertising, the sale of personal information, or profiling
        in furtherance of a decision that produces a legal or similarly
        significant effect.
      </div>
      <br></br>
      <div className="base-text">
        In order to exercise any of the above-described rights, you or an agent
        acting on your behalf must send an email to the email address below with
        the subject, “Privacy Request.”
      </div>
      <br></br>
      <div className="base-text">
        We may require you or your agent to provide us information that enables
        us to authenticate your request. If we are not able to authenticate your
        request using commercially reasonable efforts, we may decline to comply
        with your request.
      </div>
      <br></br>
      <div className="base-text">
        If we determine that we are not subject to the laws of your jurisdiction
        of residence that afford you the above-described rights, we may decline
        to act on your request.
      </div>
      <br></br>
      <div className="base-text">
        If we refuse to take action on your request, we will notify you of the
        same. If you wish to appeal our decision, please send us an email at the
        email address below with the subject, “Privacy Appeal.”
      </div>
      <br></br>
      <div className="base-text">
        <b>Links to Third-Party Websites </b>
      </div>
      <br></br>
      <div className="base-text">
        Our Platform may contain links to third-party Platforms. Such Platforms
        have separate privacy policies that you should review. We cannot take
        responsibility for the content of linked Platforms or those companies’
        data-handling practices.
      </div>
      <br></br>
      <div className="base-text">
        <b>Data Location </b>
      </div>
      <br></br>
      <div className="base-text">
        We are headquartered in the United States and our infrastructure is
        maintained within Microsoft Azure’s secure data centers, as a result,
        the information you provide to us may reside on servers or workstations
        located the United States. Azure’s data centers are protected by
        firewalls, threat detection systems and built-in redundancy. You
        understand and consent that information you provide may be transferred
        to the United States. You also understand that the data protection laws
        in the United States may be different from those of the country where
        you reside. When we transfer personal information to the United States,
        we will implement appropriate and suitable safeguards to protect such
        information as may be required by applicable data protection laws.
      </div>
      <br></br>
      <div className="base-text">
        <b>Children’s Privacy </b>
      </div>
      <br></br>
      <div className="base-text">
        Our Platform is intended for a general audience and is not directed at
        children under the age of 16. Accordingly, we do not knowingly collect
        personal information from children under age 16 through the Platform.
        Should we discover that a child under age 16 provided us with personal
        information, we will use that information only to inform the child that
        we must have parental consent before receiving such information.
      </div>
      <br></br>
      <div className="base-text">
        <b>Changes to this Policy </b>
      </div>
      <br></br>
      <div className="base-text">
        Please note that we may change this Policy from time to time. If there
        are changes to our Policy, we will post them here and update the “Last
        Updated” date at the top of this document. Continued use of the Platform
        after any changes is deemed to be acceptance of those changes.
        Accordingly, we encourage you to check the Policy periodically for
        updates.
      </div>
      <br></br>
      <div className="base-text">
        <b>Legal Disclaimer </b>
      </div>
      <br></br>
      <div className="base-text">
        Oxford Document Management provides guidance and best practices, but
        does not provide legal advice.
      </div>
      <br></br>
      <div className="base-text">
        <b>How to Contact Us </b>
      </div>
      <br></br>
      <div className="base-text">
        If there are any questions regarding this Privacy Policy, or to make a
        complaint to use regarding our handling of your information, you may
        contact us using the information below.
      </div>
      <br></br>
      <div className="base-text">
        <b>Oxford Document Management</b>
      </div>
      <div className="base-text">
        <a href="mailto:request@oxforddoc.com">request@oxforddoc.com </a>
      </div>
      <br></br>
    </div>
  );
};

export default PrivacyPolicy;
